// Chakra Icons
import { ChevronDownIcon } from '@chakra-ui/icons';
import { MdLogout } from 'react-icons/md';
// Chakra Imports
import {
  Avatar,
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  useColorMode,
  useColorModeValue,
} from '@chakra-ui/react';
// Assets
import avatar1 from 'assets/img/avatars/avatar1.png';
import avatar2 from 'assets/img/avatars/avatar2.png';
import avatar3 from 'assets/img/avatars/avatar3.png';
// Custom Icons
import {
  ArgonLogoDark,
  ArgonLogoLight,
  ChakraLogoDark,
  ChakraLogoLight,
  ProfileIcon,
  SettingsIcon,
} from 'components/Icons/Icons';
// Custom Components
import { ItemContent } from 'components/Menu/ItemContent';
import { SearchBar } from 'components/Navbars/SearchBar/SearchBar';
import { SidebarResponsive } from 'components/Sidebar/Sidebar';
import { useUserState } from 'context/UserContext';
import { useTranslation } from 'i18n';
import React from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import routes from 'routes.js';
import { useUserDispatch, signOut } from 'context/UserContext';
import logo from 'assets/svg/logo-2q.svg';

export default function HeaderLinks(props) {
  const { variant, children, fixed, scrolled, secondary, onOpen, ...rest } = props;

  const { colorMode } = useColorMode();
  const { isAuthenticated, userInfo } = useUserState();
  const userDispatch = useUserDispatch();
  const history = useHistory();
  const { lang, setLang, t } = useTranslation();

  // Chakra Color Mode — call hooks unconditionally
  const scrolledIcon = useColorModeValue('gray.700', 'gray.200');
  const unscrolledIcon = useColorModeValue('white', 'gray.200');
  let navbarIcon = fixed && scrolled ? scrolledIcon : unscrolledIcon;
  let menuBg = useColorModeValue('white', 'navy.800');
  const notificationColor = useColorModeValue('gray.700', 'white');
  // Hamburger sits on the (transparent) navbar over a near-white body bg in
  // light mode — a white icon would be invisible. Use a dark icon in light
  // mode, white in dark mode. On `secondary` pages the navbar bg is dark, so
  // white is correct there.
  const hamburgerColor = useColorModeValue('gray.700', 'white');
  if (secondary) {
    navbarIcon = 'white';
  }

  const handleToProfile = () => {
    history.push({
      pathname: '/admin/profile',
    });
  };
  return (
    <Flex pe="0px" w={{ sm: '100%', md: 'auto' }} alignItems="center" flexDirection="row">
      {/* <SearchBar me="18px" /> */}
      {/* Language toggle (EN/VI) — mirrors the public site; default English */}
      <Flex
        mr={3}
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="10px"
        overflow="hidden"
        bg="white"
        aria-label={t('admin.lang.label')}
      >
        {['en', 'vi'].map(code => (
          <Button
            key={code}
            size="sm"
            h="38px"
            px="12px"
            borderRadius="0"
            variant="ghost"
            fontWeight="semibold"
            color={lang === code ? 'brand.700' : 'gray.500'}
            bg={lang === code ? 'brand.50' : 'transparent'}
            _hover={{ bg: lang === code ? 'brand.100' : 'gray.50' }}
            onClick={() => setLang(code)}
          >
            {code.toUpperCase()}
          </Button>
        ))}
      </Flex>
      {isAuthenticated ? (
        <Flex flexDirection="row" alignItems="center" gap="10px" mr={3}>
          {/* User chip with dropdown — opens to show profile section + Hồ sơ */}
          <Menu placement="bottom-end">
            <MenuButton
              as={Button}
              variant="ghost"
              h="40px"
              px="8px"
              borderRadius="10px"
              borderWidth="1px"
              borderColor="gray.200"
              bg="white"
              _hover={{ bg: 'gray.50', borderColor: 'gray.300' }}
              _active={{ bg: 'gray.100' }}
            >
              <Flex alignItems="center" gap="10px">
                <Avatar size="xs" name={userInfo?.username} src={avatar1} borderRadius="8px" />
                <Text
                  display={{ sm: 'none', md: 'inline-flex' }}
                  fontSize="sm"
                  fontWeight="semibold"
                  color="gray.700"
                  maxW="140px"
                  noOfLines={1}
                >
                  {userInfo?.username}
                </Text>
                <ChevronDownIcon color="gray.500" boxSize="18px" />
              </Flex>
            </MenuButton>
            <MenuList
              p="0"
              minW="280px"
              bg={menuBg}
              borderRadius="14px"
              overflow="hidden"
              boxShadow="xl"
              borderWidth="1px"
              borderColor="gray.100"
            >
              {/* Profile header — gradient brand bg */}
              <Box
                p="16px"
                bgGradient="linear(to-br, brand.50, white)"
                borderBottomWidth="1px"
                borderBottomColor="gray.100"
              >
                <Flex alignItems="center" gap="12px">
                  <Avatar size="md" name={userInfo?.username} src={avatar1} borderRadius="12px" />
                  <Flex direction="column" flex="1" minW="0">
                    <Text fontWeight="bold" fontSize="sm" color="gray.800" noOfLines={1}>
                      {userInfo?.username}
                    </Text>
                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                      {userInfo?.email || 'Tài khoản quản trị'}
                    </Text>
                  </Flex>
                </Flex>
              </Box>
              {/* Menu actions */}
              <Box py="6px">
                <MenuItem
                  px="14px"
                  py="10px"
                  fontSize="sm"
                  fontWeight="medium"
                  color="gray.700"
                  _hover={{ bg: 'brand.50', color: 'brand.700' }}
                  onClick={() => handleToProfile()}
                  icon={<ProfileIcon w="18px" h="18px" />}
                >
                  Hồ sơ
                </MenuItem>
              </Box>
            </MenuList>
          </Menu>

          {/* Logout button — standalone, outside dropdown */}
          <Button
            h="40px"
            px="14px"
            borderRadius="10px"
            fontSize="sm"
            fontWeight="semibold"
            variant="outline"
            color="red.600"
            borderColor="red.200"
            bg="white"
            leftIcon={<MdLogout size={16} />}
            _hover={{ bg: 'red.50', borderColor: 'red.300', color: 'red.700' }}
            _active={{ bg: 'red.100' }}
            onClick={() => signOut(userDispatch, history)}
          >
            <Text display={{ sm: 'none', md: 'inline-flex' }}>Đăng xuất</Text>
          </Button>
        </Flex>
      ) : (
        <NavLink to="/auth/sign-in">
          <Button
            ms="0px"
            px="0px"
            me={{ sm: '2px', md: '16px' }}
            color={navbarIcon}
            variant="no-effects"
            rightIcon={
              document.documentElement.dir ? (
                ''
              ) : (
                <ProfileIcon color={navbarIcon} w="22px" h="22px" me="0px" />
              )
            }
            leftIcon={
              document.documentElement.dir ? (
                <ProfileIcon color={navbarIcon} w="22px" h="22px" me="0px" />
              ) : (
                ''
              )
            }
          >
            <Text display={{ sm: 'none', md: 'flex' }}>Đăng nhập</Text>
          </Button>
        </NavLink>
      )}

      <SidebarResponsive
        hamburgerColor={secondary ? 'white' : hamburgerColor}
        logo={
          <Stack direction="row" spacing="12px" align="center" justify="center">
            {/* {colorMode === "dark" ? (
              <ArgonLogoLight w="74px" h="27px" />
            ) : (
              <ArgonLogoDark w="74px" h="27px" />
            )}
            <Box
              w="1px"
              h="20px"
              bg={colorMode === "dark" ? "white" : "gray.700"}
            />
            {colorMode === "dark" ? (
              <ChakraLogoLight w="82px" h="21px" />
            ) : (
              <ChakraLogoDark w="82px" h="21px" />
            )} */}
            <img src={logo} alt="" />
          </Stack>
        }
        colorMode={colorMode}
        secondary={props.secondary}
        routes={routes}
        {...rest}
      />
      {/* <SettingsIcon
        cursor="pointer"
        ms={{ base: "16px", xl: "0px" }}
        me="16px"
        onClick={props.onOpen}
        color={navbarIcon}
        w="18px"
        h="18px"
      /> */}
      {/* <Menu>
        <MenuButton>
          <BellIcon color={navbarIcon} w="18px" h="18px" />
        </MenuButton>
        <MenuList p="16px 8px" bg={menuBg}>
          <Flex flexDirection="column">
            <MenuItem borderRadius="8px" mb="10px">
              <ItemContent
                time="13 minutes ago"
                info="from Alicia"
                boldInfo="New Message"
                aName="Alicia"
                aSrc={avatar1}
              />
            </MenuItem>
            <MenuItem borderRadius="8px" mb="10px">
              <ItemContent
                time="2 days ago"
                info="by Josh Henry"
                boldInfo="New Album"
                aName="Josh Henry"
                aSrc={avatar2}
              />
            </MenuItem>
            <MenuItem borderRadius="8px">
              <ItemContent
                time="3 days ago"
                info="Payment succesfully completed!"
                boldInfo=""
                aName="Kara"
                aSrc={avatar3}
              />
            </MenuItem>
          </Flex>
        </MenuList>
      </Menu> */}
    </Flex>
  );
}
